import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Eye, EyeOff, Gift } from 'lucide-react';

const LOGO_URL = "/icon-v2-512.png";

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    password: '',
    confirm_password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await register(formData.email, formData.password, formData.full_name, formData.phone);
      toast.success('Welcome to AegisPay! You received ₦100,000 demo funds!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container px-4 py-8" style={{ background: 'linear-gradient(180deg, #0B1F3A 0%, #1a3a8f 30%, #F2F5F7 30%)' }}>
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-6">
          <img 
            src={LOGO_URL} 
            alt="AegisPay Logo" 
            className="w-20 h-20 mx-auto mb-4 rounded-2xl object-cover shadow-lg"
          />
          <h1 className="text-3xl font-bold text-white">Aegis<span className="text-[#1E5EFF]">Pay</span></h1>
          <p className="text-blue-200 mt-1">Save smarter with group savings</p>
          <p className="text-blue-200/70 italic text-[11px] mt-1">Your money follows rules, not promises</p>
          <p className="text-blue-300/60 text-xs mt-0.5">app.aegispay.app</p>
        </div>

        {/* Welcome Bonus Banner */}
        <div className="mb-6 p-4 rounded-2xl bg-gradient-to-r from-[#2272C5] to-[#38B6CC] text-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-xl">
              <Gift className="w-6 h-6" />
            </div>
            <div>
              <p className="font-semibold">Welcome Bonus!</p>
              <p className="text-sm text-blue-100">Get ₦100,000 demo funds to explore</p>
            </div>
          </div>
        </div>

        <Card className="glass-card border-0 shadow-xl">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-xl text-[#0B1F3A]">Create Account</CardTitle>
            <CardDescription>Join the secure savings community</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="full_name">Full Name</Label>
                <Input
                  id="full_name"
                  name="full_name"
                  type="text"
                  placeholder="John Doe"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  className="h-12 rounded-xl bg-slate-50"
                  data-testid="register-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="h-12 rounded-xl bg-slate-50"
                  data-testid="register-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone (Optional)</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="+234 800 000 0000"
                  value={formData.phone}
                  onChange={handleChange}
                  className="h-12 rounded-xl bg-slate-50"
                  data-testid="register-phone-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="At least 6 characters"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    className="h-12 rounded-xl bg-slate-50 pr-10"
                    data-testid="register-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm_password">Confirm Password</Label>
                <Input
                  id="confirm_password"
                  name="confirm_password"
                  type="password"
                  placeholder="Re-enter your password"
                  value={formData.confirm_password}
                  onChange={handleChange}
                  required
                  className="h-12 rounded-xl bg-slate-50"
                  data-testid="register-confirm-password-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full h-12 rounded-full bg-[#0B1F3A] hover:bg-[#1a3a8f] text-white font-medium btn-press"
                disabled={loading}
                data-testid="register-submit-button"
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-slate-500">
                Already have an account?{' '}
                <Link to="/login" className="text-[#2272C5] font-medium hover:underline" data-testid="login-link">
                  Sign in
                </Link>
              </p>
            </div>

            {/* Demo Notice */}
            <div className="mt-6 p-3 rounded-xl bg-amber-50 border border-amber-200">
              <p className="text-xs text-amber-700 text-center">
                <span className="demo-badge mr-2">DEMO</span>
                This is a demo app. All funds are simulated.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Register;
